import type { QueryClient } from '@tanstack/react-query'
import type { SessionResponse } from '@fall26/shared'

/**
 * What sign-out does to the cache, and in what order.
 *
 * Extracted from the hook because the ORDER is the whole thing and it is not
 * self-evident — the obvious spelling (clear everything, then say I am signed
 * out) leaves the app on screen with the cookie already gone.
 *
 *   1. Write "not authenticated" into the session query the gate is watching.
 *      The gate's observer is bound to that query object, so writing it flips
 *      the screen in the same tick as the tap. Clearing first breaks this: a
 *      mounted observer goes on reporting the last result it saw, and there is
 *      no query left to invalidate either.
 *
 *   2. Then drop everything else. A semester of coursework must not survive in
 *      the cache after sign-out, one back-button away on a shared laptop — but
 *      the answer written in step 1 has to survive, or the gate has nothing to
 *      read.
 */
export function applySignOut(queryClient: QueryClient): void {
  queryClient.setQueryData<SessionResponse>(['session'], {
    authenticated: false,
    issuedAt: null,
    expiresAt: null,
  })
  queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' })
}
